import { type Middleware } from 'redux';
import { logActions } from 'src/redux/log/actions';
import { type AppState } from 'src/redux/types';
import { BleService } from './BleService';
import { createSelector } from '@reduxjs/toolkit';

const isLogFinalizeAction = (action: unknown): action is ReturnType<typeof logActions.liveJobLogFinalizeSuccess> => {
  return typeof action === 'object' && !!action && (action as any).type === logActions.liveJobLogFinalizeSuccess.type;
};

const destroyLogService = (filename: string) => {
  console.log('[liveJobLogMiddleware] Destroying LiveJobLogService for log:', filename);
  const svc = BleService.getJobLogService(filename);
  if (!svc) {
    console.log('[liveJobLogMiddleware] No LiveJobLogService exists for log!', filename);
    return;
  }
  svc.destroy();
  BleService.jobLogServicesByFilename.delete(filename);
};

const selectLogLocalDataByFilename = createSelector(
  (state: AppState) => state.log,
  state => state.localDataByFilename,
);
const selectLogSentinelCacheData = createSelector(
  (state: AppState) => state.sentinel,
  state => state.logs.data,
);
const selectLogLocalFilesToExist = createSelector(selectLogLocalDataByFilename, local =>
  Object.values(local).map(log => ({ id: log.id, filename: log.filename, serialNumber: log.serialNumber })),
);
const selectLogLocalFilesToRemove = createSelector(
  selectLogLocalFilesToExist,
  selectLogSentinelCacheData,
  (local, cache) =>
    local.filter(log => {
      if (!log.id || !cache[log.id]) return false;
      if (cache[log.id].status === 'complete') return true;
      return false;
    }),
);
const selectLogNextFileToRemove = createSelector(
  selectLogLocalFilesToRemove,
  (local): (typeof local)[number] | undefined => local[0],
);

let handle: number | undefined;

export const liveJobLogMiddleware: Middleware<{}, AppState> = api => next => action => {
  if (isLogFinalizeAction(action)) destroyLogService(action.payload.filename);
  const result = next(action);

  let toRemove = selectLogNextFileToRemove(api.getState());
  while (!!toRemove) {
    api.dispatch(logActions.liveJobLogFinalizeSuccess({ filename: toRemove.filename }));
    toRemove = selectLogNextFileToRemove(api.getState());
  }

  const toExist = selectLogLocalFilesToExist(api.getState());
  if (handle) window.cancelAnimationFrame(handle);
  handle = window.requestAnimationFrame(() => {
    for (const item of toExist) {
      if (BleService.jobLogServicesByFilename.has(item.filename)) continue;
      console.log('[liveJobLogMiddleware] Creating new LiveJobLogService:', item.filename);
      BleService.addJobLogService(item.filename, item.serialNumber);
    }
  });

  return result;
};
